import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiMapPin, FiCalendar, FiDollarSign, FiUsers, FiArrowRight } from 'react-icons/fi';
import { formatarData } from '../../utils/dateUtils';
import { getImageUrl } from '../../utils/imageUtils';

const Home = () => {
  const [viagens, setViagens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchViagens();
  }, []);

  const fetchViagens = async () => {
    try {
      const response = await axios.get('/api/viagens/public');
      setViagens(response.data);
    } catch (error) {
      console.error('Erro ao carregar viagens:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-sky-600 via-sky-500 to-sky-400 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Escolha seu destino. Nós cuidamos de cada detalhe.
            </h1>
            <p className="text-xl md:text-2xl text-sky-100 mb-8 max-w-3xl mx-auto">
              Transformando sonhos em experiências inesquecíveis, com organização e confiança.
            </p>
            <Link
              to="/viagens"
              className="inline-block px-8 py-4 bg-yellow-400 text-black rounded-lg font-bold text-lg hover:bg-yellow-500 transition-colors shadow-lg"
            >
              Ver Todos os Pacotes
            </Link>
          </div>
        </div>
      </section>

      {/* Viagens em Destaque */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Pacotes em Destaque</h2>
            <p className="text-xl text-gray-600">Escolha seu próximo destino</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
            </div>
          ) : viagens.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">Nenhuma viagem disponível no momento.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {viagens.map((viagem) => {
                const imagemPrincipal = viagem.imagens && Array.isArray(viagem.imagens) && viagem.imagens.length > 0
                  ? getImageUrl(viagem.imagens[0])
                  : null;

                return (
                  <div
                    key={viagem.id}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-gray-100"
                  >
                    {imagemPrincipal && (
                      <div className="h-48 bg-gray-200 overflow-hidden">
                        <img
                          src={imagemPrincipal}
                          alt={viagem.titulo}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{viagem.titulo}</h3>
                      <div className="space-y-2 mb-4 text-gray-600">
                        <div className="flex items-center">
                          <FiMapPin className="mr-2 text-sky-600" size={18} />
                          {viagem.destino}
                        </div>
                        <div className="flex items-center">
                          <FiCalendar className="mr-2 text-sky-600" size={18} />
                          {formatarData(viagem.data_ida)}
                        </div>
                        <div className="flex items-center">
                          <FiUsers className="mr-2 text-sky-600" size={18} />
                          {viagem.lotado ? (
                            <span className="text-red-600 font-semibold">Lotado - Não há mais poltronas</span>
                          ) : (
                            <span>{viagem.vagas_disponiveis || viagem.vagas || 0} poltronas disponíveis</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div>
                          <p className="text-sm text-gray-500">A partir de</p>
                          <p className="text-3xl font-bold text-sky-600">
                            R$ {parseFloat(viagem.valor || 0).toFixed(2)}
                          </p>
                        </div>
                        {viagem.lotado ? (
                          <div className="flex items-center px-6 py-3 bg-gray-400 text-white rounded-lg cursor-not-allowed font-medium">
                            Lotado
                          </div>
                        ) : (
                          <Link
                            to={`/viagem/${viagem.id}`}
                            className="flex items-center px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors font-medium"
                          >
                            Ver Detalhes
                            <FiArrowRight className="ml-2" size={18} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-400 to-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-4">
            Pronto para sua próxima aventura?
          </h2>
          <p className="text-xl text-black/80 mb-8">
            Entre em contato conosco e garante sua viagem dos sonhos
          </p>
          <Link
            to="/viagens"
            className="inline-block px-8 py-4 bg-black text-white rounded-lg font-bold text-lg hover:bg-gray-800 transition-colors"
          >
            Ver Todos os Pacotes
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
